import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { Loader2 } from "lucide-react";

const PROJECT_TYPES = [
  "Interior Repainting",
  "Exterior Repainting",
  "Full Home Repaint",
  "Accent Wall Refresh",
  "Damp-Patch Repainting",
];

const VISIT_SLOTS = ["10 AM – 12 PM", "12 – 2 PM", "2 – 4 PM", "4 – 6 PM"];

export default function QuoteDialog({ open, onOpenChange, prefill }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    project_type: "",
    area: "",
    message: "",
    preferred_date: "",
    preferred_slot: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        project_type: prefill?.project_type || f.project_type,
        area: prefill?.area || f.area,
        message: prefill?.message || f.message,
      }));
    }
  }, [open, prefill]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.project_type) {
      toast.error("Please fill name, phone and project type.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createLead({
        ...form,
        source: "quote-popup",
        estimated_cost: prefill?.estimated_cost,
      });
      toast.success(
        res?.promo_code
          ? `Your 25% code: ${res.promo_code} — we'll WhatsApp you shortly.`
          : "Got it! Our team will call you within 30 minutes."
      );
      onOpenChange(false);
      setForm({ name: "", phone: "", project_type: "", area: "", message: "", preferred_date: "", preferred_slot: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl"
        data-testid="quote-dialog"
      >
        <DialogHeader>
          <div className="inline-flex self-start items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-[11px] font-bold tracking-widest uppercase text-orange-700">
            Free on-site visit
          </div>
          <DialogTitle className="font-display text-2xl text-ink">
            Get your free painting quote
          </DialogTitle>
          <DialogDescription>
            Fill in your details — we&apos;ll call within 30 minutes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="quote-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qd-name" className="text-xs font-bold text-ink">
                Name
              </Label>
              <Input
                id="qd-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="quote-input-name"
              />
            </div>
            <div>
              <Label htmlFor="qd-phone" className="text-xs font-bold text-ink">
                Phone
              </Label>
              <Input
                id="qd-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 98xxx xxxxx"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="quote-input-phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-ink">Project type</Label>
              <Select
                value={form.project_type}
                onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}
              >
                <SelectTrigger
                  className="mt-1.5 h-11 rounded-xl"
                  data-testid="quote-select-project-type"
                >
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {form.project_type &&
                    !PROJECT_TYPES.includes(form.project_type) && (
                      <SelectItem value={form.project_type}>
                        {form.project_type}
                      </SelectItem>
                    )}
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="qd-area" className="text-xs font-bold text-ink">
                Area (sq.ft)
              </Label>
              <Input
                id="qd-area"
                value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
                placeholder="e.g., 1200"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="quote-input-area"
              />
            </div>
          </div>

          {prefill?.estimated_cost ? (
            <div className="rounded-xl bg-orange-50 text-orange-900 px-4 py-3 text-sm font-bold">
              Estimated cost: ₹
              {new Intl.NumberFormat("en-IN").format(prefill.estimated_cost)}
            </div>
          ) : null}

          <div>
            <Label className="text-xs font-bold text-ink">
              Free site visit <span className="font-normal text-muted-ink">(optional)</span>
            </Label>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={form.preferred_date}
                onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                className="h-11 rounded-xl"
                data-testid="quote-visit-date"
              />
              <Select
                value={form.preferred_slot}
                onValueChange={(v) => setForm((f) => ({ ...f, preferred_slot: v }))}
              >
                <SelectTrigger className="h-11 rounded-xl" data-testid="quote-visit-slot">
                  <SelectValue placeholder="Preferred time slot" />
                </SelectTrigger>
                <SelectContent>
                  {VISIT_SLOTS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit"
              className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-brand"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                "Submit & Get Callback"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
